/**
 * Nachtrags-Basisauswahl — Ableitung der Auswahlliste aus /api/quote-data.
 *
 * Hintergrund (Ollis Produktions-DB): Angebotsnummern sind dort NICHT eindeutig
 * (z.B. A26/00025 zehnmal vergeben — mit identischem Kunden, Bauvorhaben,
 * Datum und Positionszahl; unterscheidbar NUR über die Angebotssumme). Die
 * frühere Auswahl in header.tsx faltete die Liste über
 * `new Set(files.map(f => f.offerNumber))` zusammen — mehrere Angebote mit
 * derselben Nummer wurden EIN Eintrag, der Rest war gar nicht auswählbar.
 *
 * Dieses Modul liefert stattdessen JE ANGEBOT (Slot) einen Eintrag mit allen
 * Feldern, die zur Identifikation nötig sind (inkl. Angebotssumme und der
 * eindeutigen Ablage-Nr. = Slot), und markiert mehrfach vergebene Nummern.
 *
 * Nachträge (Nummern der Form <Basis>/N<n>) sind KEINE wählbare Basis: ein
 * Nachtrag auf einen Nachtrag ergäbe …/N1/N1 statt …/N2 (Ollis Vorfall vom
 * 24.07., Zählerzeile A26/00025/N1). Stattdessen erscheinen vorhandene
 * Nachträge als Hinweis AM Basisangebot ("hat bereits N1") — zugeordnet über
 * nachtragZuSlot, wenn der Nachtrag ihn trägt, sonst über die Nummern-Wurzel
 * an allen gleichnamigen Angeboten (ehrlich: ohne Slot ist die Kopie nicht
 * bestimmbar).
 *
 * Reine Ableitungslogik ohne React/Fetch — testbar mit tsx --test
 * (Muster quote-position-copy.ts / ifc-import-mapping.ts).
 */

import {
  findDuplicateOfferNumbers,
  isNachtragNumber,
  nachtragBaseRoot,
} from '@shared/quote-number-circle';

/** Roh-Eintrag aus GET /api/quote-data (result.data.files). */
export interface NachtragBaseCandidate {
  slot: number;
  offerNumber?: string;
  customerName?: string;
  projectName?: string;
  timestamp?: string;
  itemCount?: number;
  /** Netto-Angebotssumme aus den Metadaten — bei Ollis zehn Namensvettern das einzige Unterscheidungsmerkmal. */
  totalValue?: number;
  /** Nachtrags-Verknüpfung des Eintrags selbst (nur bei Nachträgen gesetzt). */
  nachtragZu?: string;
  nachtragZuSlot?: number;
}

export interface NachtragBaseEntry {
  /** Stabile Kennung des Angebots — Grundlage der eindeutigen Verknüpfung UND als Ablage-Nr. sichtbar. */
  slot: number;
  offerNumber: string;
  customerName: string;
  projectName: string;
  timestamp?: string;
  itemCount: number | null;
  /** Netto-Angebotssumme; null, wenn nicht ermittelbar. */
  totalValue: number | null;
  /** true = diese Nummer ist im Bestand mehrfach vergeben (Kennzeichnungspflicht). */
  nummerMehrfachVergeben: boolean;
  /**
   * Vorhandene Nachträge zu diesem Angebot, als Suffixe ("N1", "N2").
   * Beantwortet Ollis "wo ich ja Nachtrag 1 schon gemacht habe" direkt in der
   * Zeile. Ohne Slot am Nachtrag steht der Hinweis an ALLEN gleichnamigen
   * Angeboten — welche Kopie gemeint war, ist dann aus den Daten nicht bestimmbar.
   */
  vorhandeneNachtraege: string[];
}

/** Suffix-Sortierung N1 < N2 < N10 (numerisch, nicht lexikografisch). */
function sortNachtragSuffixe(suffixe: Iterable<string>): string[] {
  return Array.from(new Set(suffixe)).sort((a, b) => {
    const na = Number((a.match(/^N(\d+)/) || [])[1] || 0);
    const nb = Number((b.match(/^N(\d+)/) || [])[1] || 0);
    if (na !== nb) return na - nb;
    return a.localeCompare(b, 'de');
  });
}

/**
 * Baut die Auswahlliste: ein Eintrag je Angebot (KEIN Set über Nummern).
 *
 * - Entwürfe (`Entwurf-…`) und Angebote ohne Nummer sind keine Nachtrags-Basis.
 * - NACHTRÄGE (<Basis>/N<n>) sind keine Basis — sie werden als Hinweis am
 *   Basisangebot geführt (vorhandeneNachtraege), nie als eigene Zeile.
 * - Das aktuell geöffnete Angebot wird über seinen SLOT ausgeschlossen — nicht
 *   über die Nummer: bei mehrfach vergebenen Nummern würde der Nummernvergleich
 *   auch die gleichnamigen ANDEREN Angebote verschlucken.
 * - Mehrfach vergebene Nummern werden über den GESAMTEN Basis-Bestand erkannt
 *   (inklusive des aktuellen Angebots), damit die Kennzeichnung auch dann
 *   stimmt, wenn einer der Namensvettern gerade offen ist.
 * - Sortierung: Nummer, dann Summe (Namensvettern mit gleicher Summe stehen
 *   als Block beieinander), dann neuestes zuerst, dann Slot.
 */
export function buildNachtragBaseEntries(
  files: ReadonlyArray<NachtragBaseCandidate>,
  currentSlot: number | null,
): NachtragBaseEntry[] {
  const withNumber = (files || [])
    .filter((f): f is NachtragBaseCandidate => !!f && Number.isFinite(f.slot))
    .map((f) => ({ ...f, offerNumber: (f.offerNumber || '').trim() }))
    .filter((f) => f.offerNumber && !f.offerNumber.startsWith('Entwurf-'));

  const nachtraege = withNumber.filter((f) => isNachtragNumber(f.offerNumber));
  const basen = withNumber.filter((f) => !isNachtragNumber(f.offerNumber));

  const duplicates = findDuplicateOfferNumbers(basen.map((f) => f.offerNumber));

  // Hinweise "hat bereits N1": je Nachtrag die Wurzel bestimmen und dem
  // Basisangebot zuordnen — punktgenau über nachtragZuSlot, sonst über die
  // Nummer an alle Namensvettern.
  const baseNumberBySlot = new Map<number, string>();
  for (const b of basen) baseNumberBySlot.set(b.slot, b.offerNumber!);

  const hinweiseJeSlot = new Map<number, Set<string>>();
  const hinweiseJeNummer = new Map<string, Set<string>>();
  for (const n of nachtraege) {
    const root = nachtragBaseRoot(n.offerNumber!);
    if (!root || root === n.offerNumber) continue;
    const suffix = n.offerNumber!.slice(root.length + 1); // "N1" bzw. verschachtelt "N1/N1"
    const zuSlot = n.nachtragZuSlot;
    if (
      typeof zuSlot === 'number' &&
      Number.isInteger(zuSlot) &&
      baseNumberBySlot.get(zuSlot) === root
    ) {
      if (!hinweiseJeSlot.has(zuSlot)) hinweiseJeSlot.set(zuSlot, new Set());
      hinweiseJeSlot.get(zuSlot)!.add(suffix);
    } else {
      if (!hinweiseJeNummer.has(root)) hinweiseJeNummer.set(root, new Set());
      hinweiseJeNummer.get(root)!.add(suffix);
    }
  }

  return basen
    .filter((f) => currentSlot === null || f.slot !== currentSlot)
    .map((f) => ({
      slot: f.slot,
      offerNumber: f.offerNumber!,
      customerName: (f.customerName || '').trim(),
      projectName: (f.projectName || '').trim(),
      timestamp: f.timestamp,
      itemCount: typeof f.itemCount === 'number' && Number.isFinite(f.itemCount) ? f.itemCount : null,
      totalValue:
        typeof f.totalValue === 'number' && Number.isFinite(f.totalValue) ? f.totalValue : null,
      nummerMehrfachVergeben: duplicates.has(f.offerNumber!),
      vorhandeneNachtraege: sortNachtragSuffixe([
        ...(hinweiseJeSlot.get(f.slot) ?? []),
        ...(hinweiseJeNummer.get(f.offerNumber!) ?? []),
      ]),
    }))
    .sort((a, b) => {
      const byNumber = a.offerNumber.localeCompare(b.offerNumber, 'de');
      if (byNumber !== 0) return byNumber;
      // Gleiche Nummer: nach Summe gruppieren (Ollis drei Summen-Blöcke stehen
      // beieinander), Einträge ohne Summe ans Ende der Gruppe.
      if (a.totalValue !== b.totalValue) {
        if (a.totalValue === null) return 1;
        if (b.totalValue === null) return -1;
        return a.totalValue - b.totalValue;
      }
      // Gleiche Summe: neuestes zuerst, dann Slot — deterministische Reihenfolge.
      const byTime = String(b.timestamp || '').localeCompare(String(a.timestamp || ''));
      if (byTime !== 0) return byTime;
      return a.slot - b.slot;
    });
}

/** Suche über Nummer UND Kunde UND Bauvorhaben (Ollis "such mich dumm und dämlich"). */
export function filterNachtragBaseEntries(
  entries: ReadonlyArray<NachtragBaseEntry>,
  search: string,
): NachtragBaseEntry[] {
  const term = (search || '').trim().toLowerCase();
  if (!term) return [...entries];
  return entries.filter(
    (e) =>
      e.offerNumber.toLowerCase().includes(term) ||
      e.customerName.toLowerCase().includes(term) ||
      e.projectName.toLowerCase().includes(term),
  );
}

/** Deutsche Währungsanzeige der Angebotssumme (210465.22 → "210.465,22 €"); leer bei unbekannter Summe. */
export function formatiereSumme(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}
